import { NewExperimentsType } from '@/stores/experiment.store';
import { ExperimentInfo } from '../types';
import { getPreferredExperimentAndCleanUpUrl, isBackendExperiment, isExperimentInConfig } from '../experiment-helper';
import {
	useExperimentStore,
	useLanguageStore,
	useReadyStore,
	useRoutingStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { watch } from 'vue';
import { UserState } from '@/stores/user.store';
import { objectKeys } from '@/helpers/object-helper';
import { ReadyType } from '@/stores/ready.store';

const isExperimentInitialized = false;
export function useExperimentInitialization() {
	const {
		activatedExperiments,
		_preferredExperiments,
		setAcquiredExperiments,
		setActivatedExperiments,
		setNewPreferredExperiments,
	} = useUserStore();
	const { country, language } = useLanguageStore();
	const { queryParams } = useRoutingStore();
	const { setNewExperiments } = useExperimentStore();
	const { newExperimentsLoaded, setReady } = useReadyStore();

	function syncAvailableExperiments(newExperiments: NewExperimentsType[]) {
		const acquiredExperiments: ExperimentInfo[] = newExperiments
			/*
			 * - Countries: either no countries specified in the config or includes the current country
			 */
			.filter(el => el.countries?.length === 0 || el.countries?.includes(country.value))
			/*
			 * - Languages: either no languages specified in the config or includes the current language
			 */
			.filter(el => el.languages?.length === 0 || el.languages?.includes(language.value))
			.map(el => ({
				name: el.name as ExperimentInfo['name'],
				variant: el.variant as ExperimentInfo['variant'],
			}));

		const preferredExperiments = getPreferredExperimentAndCleanUpUrl(queryParams.value, acquiredExperiments);

		cleanAndPersistExperiments(acquiredExperiments, preferredExperiments);
	}

	function cleanAndPersistExperiments(
		initialAcquiredExperiments: ExperimentInfo[],
		preferredExperiments: ExperimentInfo[]
	) {
		/**
		 * Build a map of acquired experiments:
		 * - Only include backend experiments (prefixed with 'BE_') or those defined in `experimentConfig`
		 * - Format: { [experimentName]: variant }
		 */
		const acquiredExperimentsMap: UserState['acquiredExperiments'] = initialAcquiredExperiments.reduce(
			(acc, exp) => {
				if (isBackendExperiment(exp.name) || isExperimentInConfig(exp.name)) {
					acc[exp.name] = exp.variant;
				}

				return acc;
			},
			{} as UserState['acquiredExperiments']
		);

		/**
		 * Merge existing preferred experiments with new ones from payload:
		 * - The array is transformed into an object directly
		 */
		const mergedPreferredExperiments = {
			...(_preferredExperiments.value as UserState['_preferredExperiments']),
			...preferredExperiments.reduce((acc, exp) => {
				acc[exp.name] = exp.variant;
				return acc;
			}, {} as UserState['_preferredExperiments']),
		};

		/**
		 * Validate preferred experiments:
		 * - Keep only if the experiment exists in config or is part of backend-acquired ones
		 */
		for (const name of objectKeys(mergedPreferredExperiments)) {
			const isBackendAvailable = isBackendExperiment(name) && acquiredExperimentsMap[name];

			if (!isExperimentInConfig(name) && !isBackendAvailable) {
				delete mergedPreferredExperiments[name];
			}
		}

		/**
		 * Filter activated experiments to only those present in the experiment config
		 */
		const validatedActivatedExperiments: string[] = (
			activatedExperiments.value as UserState['activatedExperiments']
		).filter(entry => {
			const [experimentName] = entry.split(':');
			return isExperimentInConfig(experimentName);
		});

		/**
		 * Initialize the experiment store with validated data
		 */

		setAcquiredExperiments(acquiredExperimentsMap);
		setNewPreferredExperiments(mergedPreferredExperiments);
		setActivatedExperiments(validatedActivatedExperiments);
		if (!newExperimentsLoaded.value) {
			setReady(ReadyType.NEW_EXPERIMENTS_LOADED);
		}
	}

	function initializeExperiments(newExperiments: NewExperimentsType[]) {
		if (isExperimentInitialized) return;
		setNewExperiments(newExperiments);
		watch(
			() => [language.value, country.value],
			() => {
				syncAvailableExperiments(newExperiments);
			},
			{ immediate: true }
		);
	}

	return {
		initializeExperiments,
	};
}
